import { BRAND_LEARNING_SOURCE_PREFERENCE } from '@mondaygpt/constants'
import type { BrandLearningSourceSummary, BrandLearningSummary } from '@mondaygpt/types'

/**
 * Generic per-source priority picker.
 *
 * The visual + voice sections both want the same fallback ladder. Order is
 * sourced from `BRAND_LEARNING_SOURCE_PREFERENCE` in `@mondaygpt/constants`
 * — the same ladder the server's `BrandResolver` uses, so a new miner on
 * the server side is automatically picked up here too. Without this hoist,
 * v1's ['instagram','website'] would silently leave any v2 source (tiktok,
 * linkedin, …) unranked on the client even after the server started serving
 * rows for it.
 *
 * Returns `null` when the summary itself is null — callers render the empty
 * state.
 */
function pickPreferredSource(
  summary: BrandLearningSummary | null
): BrandLearningSourceSummary | null {
  if (!summary) return null
  for (const source of BRAND_LEARNING_SOURCE_PREFERENCE) {
    const match = summary.sources.find(s => s.source === source && s.aggregates !== null)
    if (match) return match
  }
  // Fall back to any source with aggregates, then any source at all.
  const withAggregates = summary.sources.find(s => s.aggregates !== null)
  if (withAggregates) return withAggregates
  return summary.sources[0] ?? null
}

/**
 * Pick the per-source summary the visual ("How you look") section should show.
 * Consumed by `BrandCardStep` (onboarding) and `BrandKitPage` (settings) —
 * keep the priority order consistent across surfaces.
 */
export function pickPreferredVisualSource(
  summary: BrandLearningSummary | null
): BrandLearningSourceSummary | null {
  return pickPreferredSource(summary)
}

/**
 * Pick the per-source summary the voice ("How you sound") section should show.
 * Mirrors {@link pickPreferredVisualSource} — same priority order, named
 * separately so call-sites read closer to the section they're driving.
 */
export function pickPreferredVoiceSource(
  summary: BrandLearningSummary | null
): BrandLearningSourceSummary | null {
  return pickPreferredSource(summary)
}
